import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Alert,
} from "reactstrap";
import classnames from "classnames";
import DataTable from "react-data-table-component";
import Loader from "../../components/Common/Loader";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import { GetData, PatchData, PostData } from "../../services/api";

const EmailProviders = () => {
  const [activeTab, setActiveTab] = useState("connections");
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [savingProvider, setSavingProvider] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [creatingDomain, setCreatingDomain] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [providers, setProviders] = useState([]);
  const [selectedProviderKey, setSelectedProviderKey] = useState("");

  const [providerConfigs, setProviderConfigs] = useState({});
  const [domains, setDomains] = useState([]);

  const [newDomain, setNewDomain] = useState("");
  const [dnsRecordsForDomain, setDnsRecordsForDomain] = useState(null);

  // Clave propia de un dominio. Hace falta cuando los dominios de la empresa
  // viven en CUENTAS distintas del proveedor: la clave de la empresa autoriza
  // solo los dominios de su cuenta y el otro responde "Forbidden".
  const [claveDominio, setClaveDominio] = useState(null); // { id, domain, valor }
  const [guardandoClave, setGuardandoClave] = useState(false);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  const loadProviders = async () => {
    setLoadingProviders(true);
    try {
      // Verificar si la URL de la API está configurada
      if (!process.env.REACT_APP_API) {
        showMessage("warning", "La URL de la API no está configurada. Configura REACT_APP_API en tu archivo .env");
        setLoadingProviders(false);
        return;
      }

      const response = await GetData("/email/providers");
      const data = response?.data;
      const list = Array.isArray(data) ? data : [];
      setProviders(list);
      if (!selectedProviderKey && list.length > 0) {
        setSelectedProviderKey(list[0].key || list[0].id || list[0].name);
      }
      if (list.length === 0) {
        showMessage("info", "No hay proveedores configurados. El backend aún no ha implementado este endpoint.");
      }
    } catch (e) {
      console.error("Error loading providers", e);
      const errorMessage = e.message || "Error desconocido";
      if (errorMessage.includes("conectar") || errorMessage.includes("Network")) {
        showMessage("danger", `No se pudo conectar con el backend. Verifica que el servidor esté corriendo en ${process.env.REACT_APP_API || "la URL configurada"}`);
      } else if (errorMessage.includes("404") || errorMessage.includes("no encontrado")) {
        showMessage("warning", "El endpoint /email/providers no existe en el backend. Necesitas implementarlo.");
      } else {
        showMessage("danger", `Error: ${errorMessage}`);
      }
    } finally {
      setLoadingProviders(false);
    }
  };

  const loadProviderConfig = async (providerKey) => {
    if (!providerKey) return;
    try {
      const { data } = await GetData(`/email/providers/${providerKey}`);
      setProviderConfigs((prev) => ({ ...prev, [providerKey]: data || {} }));
    } catch (e) {
      console.error("Error loading provider config", e);
    }
  };

  const loadDomains = async (providerKey) => {
    if (!providerKey) return;
    setLoadingDomains(true);
    try {
      const { data } = await GetData(`/email/providers/${providerKey}/domains`);
      const domainsList = Array.isArray(data) ? data : [];
      setDomains(domainsList);
      
      // Si hay un dominio seleccionado para mostrar DNS, mantenerlo actualizado
      if (dnsRecordsForDomain) {
        const updatedDomain = domainsList.find(
          (d) => d.domain === dnsRecordsForDomain.domain
        );
        if (updatedDomain && updatedDomain.dnsRecords) {
          setDnsRecordsForDomain({
            ...dnsRecordsForDomain,
            dnsRecords: updatedDomain.dnsRecords,
          });
        }
      }
    } catch (e) {
      console.error("Error loading domains", e);
      showMessage("danger", "Error al cargar los dominios.");
    } finally {
      setLoadingDomains(false);
    }
  };

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    if (selectedProviderKey) {
      loadProviderConfig(selectedProviderKey);
      if (activeTab === "domains") {
        loadDomains(selectedProviderKey);
      }
      setDnsRecordsForDomain(null);
    }
  }, [selectedProviderKey, activeTab]);

  const handleChangeConfigField = (providerKey, field, value) => {
    setProviderConfigs((prev) => ({
      ...prev,
      [providerKey]: {
        ...(prev[providerKey] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveProvider = async (providerKey) => {
    if (!providerKey) return;
    const payload = providerConfigs[providerKey] || {};
    setSavingProvider(true);
    try {
      await PostData(`/email/providers/${providerKey}`, payload);
      showMessage("success", "Credenciales guardadas correctamente.");
      await loadProviders();
      await loadProviderConfig(providerKey);
    } catch (e) {
      console.error("Error saving provider config", e);
      showMessage("danger", "Error al guardar las credenciales.");
    } finally {
      setSavingProvider(false);
    }
  };

  const handleTestConnection = async (providerKey) => {
    if (!providerKey) return;
    setTestingConnection(true);
    try {
      await PostData(`/email/providers/${providerKey}/test`, {});
      showMessage("success", "Conexión exitosa con el proveedor.");
      await loadProviders();
    } catch (e) {
      console.error("Error testing provider connection", e);
      showMessage("danger", "Error al probar la conexión. Verifica tus credenciales.");
    } finally {
      setTestingConnection(false);
    }
  };

  const handleCreateDomain = async (e) => {
    e.preventDefault();
    if (!selectedProviderKey || !newDomain) return;
    
    setCreatingDomain(true);
    setDnsRecordsForDomain(null);
    
    try {
      const { data } = await PostData(
        `/email/providers/${selectedProviderKey}/domains`,
        { domain: newDomain }
      );
      if (data && data.dnsRecords) {
        setDnsRecordsForDomain(data);
        showMessage("success", `Dominio ${newDomain} creado exitosamente. Configura los registros DNS a continuación.`);
      } else {
        setDnsRecordsForDomain(null);
        showMessage("info", "Dominio creado, pero no se recibieron registros DNS.");
      }
      setNewDomain("");
      await loadDomains(selectedProviderKey);
    } catch (err) {
      console.error("Error creating domain", err);
      
      // Extraer mensaje de error del backend
      let errorMessage = "Error al crear el dominio. Verifica que el proveedor esté configurado correctamente.";
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      showMessage("danger", errorMessage);
    } finally {
      setCreatingDomain(false);
    }
  };

  const handleRefreshDomainStatus = async (domainIdOrName) => {
    if (!selectedProviderKey || !domainIdOrName) return;
    try {
      const { data } = await GetData(
        `/email/providers/${selectedProviderKey}/domains/${domainIdOrName}/status`
      );
      await loadDomains(selectedProviderKey);
      if (data && data.dnsRecords) {
        setDnsRecordsForDomain(data);
      }
      showMessage("success", "Estado del dominio actualizado.");
    } catch (err) {
      console.error("Error refreshing domain status", err);
      showMessage("danger", "Error al verificar el estado del dominio.");
    }
  };

  const handleViewDNSRecords = async (domainIdOrName, domainName) => {
    if (!selectedProviderKey || !domainIdOrName) return;
    
    // Mostrar loader mientras se obtienen los registros DNS
    setCreatingDomain(true);
    
    try {
      // Siempre obtener los registros DNS más recientes del proveedor
      // Esto asegura que los registros estén actualizados aunque cambien en el proveedor
      const { data } = await GetData(
        `/email/providers/${selectedProviderKey}/domains/${domainIdOrName}/status`
      );
      
      if (data && data.dnsRecords && data.dnsRecords.length > 0) {
        setDnsRecordsForDomain({
          domain: domainName || data.domain,
          dnsRecords: data.dnsRecords,
        });
        showMessage("success", `Registros DNS actualizados para ${domainName || data.domain}`);
        
        // Actualizar la lista de dominios para reflejar los cambios
        await loadDomains(selectedProviderKey);
      } else {
        // Si no hay registros DNS en la respuesta, intentar obtenerlos del dominio guardado
        const domain = domains.find(
          (d) => (d.id === domainIdOrName) || (d.domain === domainIdOrName) || (d.domain === domainName)
        );
        
        if (domain && domain.dnsRecords && domain.dnsRecords.length > 0) {
          setDnsRecordsForDomain({
            domain: domainName || domain.domain,
            dnsRecords: domain.dnsRecords,
          });
          showMessage("info", `Registros DNS para ${domainName || domain.domain} (desde base de datos)`);
        } else {
          showMessage("warning", "No se encontraron registros DNS para este dominio. El proveedor puede no haberlos generado aún.");
        }
      }
    } catch (err) {
      console.error("Error getting DNS records", err);
      
      // Si falla, intentar obtener los registros del dominio guardado en la base de datos
      const domain = domains.find(
        (d) => (d.id === domainIdOrName) || (d.domain === domainIdOrName) || (d.domain === domainName)
      );
      
      if (domain && domain.dnsRecords && domain.dnsRecords.length > 0) {
        setDnsRecordsForDomain({
          domain: domainName || domain.domain,
          dnsRecords: domain.dnsRecords,
        });
        showMessage("warning", `Registros DNS para ${domainName || domain.domain} (no se pudo actualizar desde el proveedor)`);
      } else {
        let errorMessage = "Error al obtener los registros DNS del dominio.";
        if (err.message) {
          errorMessage = err.message;
        } else if (err.response?.data?.error?.message) {
          errorMessage = err.response.data.error.message;
        }
        showMessage("danger", errorMessage);
      }
    } finally {
      setCreatingDomain(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showMessage("success", "Copiado al portapapeles");
    });
  };

  const providerConnectionsColumns = [
    {
      name: <span className="font-weight-bold fs-13">Proveedor</span>,
      selector: (row) => row.displayName || row.name || row.key,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Estado</span>,
      selector: (row) => row.status || "N/A",
      sortable: true,
      cell: (row) => {
        const status = (row.status || "").toLowerCase();
        let badgeClass = "badge bg-secondary";
        let statusText = "Sin configurar";
        if (status === "connected") {
          badgeClass = "badge bg-success";
          statusText = "Conectado";
        } else if (status === "error") {
          badgeClass = "badge bg-danger";
          statusText = "Error";
        } else if (status === "not_configured") {
          badgeClass = "badge bg-warning text-dark";
          statusText = "Sin configurar";
        }
        return (
          <span className={badgeClass}>
            {statusText}
          </span>
        );
      },
    },
  ];

  const guardarClaveDominio = async () => {
    if (!claveDominio) return;
    setGuardandoClave(true);
    try {
      const { data } = await PatchData(
        `/email/providers/${selectedProviderKey}/domains/${claveDominio.id}`,
        { apiKey: claveDominio.valor || "" }
      );
      // Si el backend avisa de algo -p. ej. que la clave es la misma que la de
      // la empresa y por tanto no cambia nada- se muestra como advertencia, no
      // como éxito: guardó, pero no resuelve el problema.
      if (data?.aviso) {
        showMessage("warning", data.aviso);
      } else {
        showMessage(
          "success",
          data?.tieneClavePropia
            ? `Clave propia guardada para ${claveDominio.domain}.`
            : `Se quitó la clave propia de ${claveDominio.domain}: volverá a usar la de la empresa.`
        );
      }
      setClaveDominio(null);
      await loadDomains(selectedProviderKey);
    } catch (e) {
      showMessage(
        "danger",
        e?.response?.data?.error?.message || "No se pudo guardar la clave."
      );
    } finally {
      setGuardandoClave(false);
    }
  };

  const domainColumns = [
    {
      name: <span className="font-weight-bold fs-13">Dominio</span>,
      selector: (row) => row.domain || row.name,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Estado</span>,
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        const status = (row.status || "").toLowerCase();
        const isVerified = status === "verified" || status === "success";
        return (
          <span>
            <i
              className={
                "mdi " +
                (isVerified
                  ? "mdi-check-circle text-success"
                  : "mdi-alert-circle-outline text-warning")
              }
            ></i>{" "}
            {isVerified ? "Verificado" : "Pendiente"}
          </span>
        );
      },
    },
    {
      name: <span className="font-weight-bold fs-13">Clave</span>,
      cell: (row) =>
        row.tieneClavePropia ? (
          <span title="Este dominio usa su propia clave del proveedor">
            <i className="mdi mdi-key-variant text-success"></i> Propia
          </span>
        ) : (
          <span className="text-muted" title="Usa la clave configurada en el proveedor de la empresa">
            <i className="mdi mdi-key-outline"></i> De la empresa
          </span>
        ),
    },
    {
      name: <span className="font-weight-bold fs-13">Acciones</span>,
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            color="info"
            size="sm"
            onClick={() =>
              handleViewDNSRecords(
                row.id || row.domain || row.name,
                row.domain || row.name
              )
            }
            title="Ver registros DNS"
          >
            <i className="mdi mdi-dns me-1"></i>
            Ver DNS
          </Button>
          <Button
            color="primary"
            size="sm"
            onClick={() =>
              handleRefreshDomainStatus(row.id || row.domain || row.name)
            }
            title="Verificar estado del dominio"
          >
            <i className="mdi mdi-refresh me-1"></i>
            Verificar
          </Button>
          <Button
            color={row.tieneClavePropia ? "success" : "soft-secondary"}
            size="sm"
            onClick={() =>
              setClaveDominio({
                id: row.id,
                domain: row.domain || row.name,
                valor: "",
              })
            }
            title="Clave propia de este dominio"
          >
            <i className="mdi mdi-key-variant me-1"></i>
            Clave
          </Button>
        </div>
      ),
    },
  ];

  const renderProviderFormFields = (providerKey) => {
    const config = providerConfigs[providerKey] || {};
    const meta =
      providers.find(
        (p) => p.key === providerKey || p.id === providerKey || p.name === providerKey
      ) || {};
    const dynamicFields = Array.isArray(meta.fields) ? meta.fields : [];

    if (dynamicFields.length > 0) {
      return dynamicFields.map((field) => (
        <FormGroup key={field.name} className="mb-3">
          <Label className="form-label">
            {field.label || field.name}
            {field.type === "secret" && (
              <span className="text-muted ms-1">(secreto)</span>
            )}
          </Label>
          <Input
            type={field.type === "secret" ? "password" : "text"}
            value={config[field.name] || ""}
            onChange={(e) =>
              handleChangeConfigField(providerKey, field.name, e.target.value)
            }
            placeholder={field.placeholder || `Ingresa ${field.label || field.name}`}
          />
          {field.help && (
            <small className="form-text text-muted">{field.help}</small>
          )}
        </FormGroup>
      ));
    }

    // Fallback: campos genéricos para cualquier proveedor
    return (
      <>
        <Alert color="info" className="mb-3">
          <i className="mdi mdi-information-outline me-2"></i>
          Ingresa las credenciales de tu proveedor de email. Estas credenciales se guardan de forma segura y solo se usan para enviar correos.
        </Alert>
        <FormGroup className="mb-3">
          <Label className="form-label">
            API Key / Token <span className="text-danger">*</span>
          </Label>
          <Input
            type="password"
            value={config.apiKey || config.token || ""}
            onChange={(e) =>
              handleChangeConfigField(providerKey, config.apiKey ? "apiKey" : "token", e.target.value)
            }
            placeholder="Ingresa la API key o token del proveedor"
          />
          <small className="form-text text-muted">
            Encuentra esta clave en el panel de control de tu proveedor (Mailgun, AWS SES, etc.)
          </small>
        </FormGroup>
        <FormGroup className="mb-3">
          <Label className="form-label">Región / Endpoint</Label>
          <Input
            type="text"
            value={config.region || config.endpoint || ""}
            onChange={(e) =>
              handleChangeConfigField(providerKey, config.region ? "region" : "endpoint", e.target.value)
            }
            placeholder="ej. us-east-1, eu, api.mailgun.net"
          />
          <small className="form-text text-muted">
            Opcional. Solo necesario para algunos proveedores como AWS SES.
          </small>
        </FormGroup>
      </>
    );
  };

  const selectedProvider = providers.find(
    (p) => p.key === selectedProviderKey || p.id === selectedProviderKey || p.name === selectedProviderKey
  );

  return (
    <div className="page-content">
      {/* Loader de pantalla completa al crear dominio */}
      {creatingDomain && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Loader message="Creando dominio..." size={200} />
          <p className="text-white-50 mt-2" style={{ fontSize: "14px" }}>
            Esto puede tardar unos segundos
          </p>
        </div>
      )}

      <Container fluid={true}>
        <Breadcrumbs title="IHubG" breadcrumbItem="Proveedores de Email" />

        {message.text && (
          <Alert color={message.type} className="mt-2">
            {message.text}
          </Alert>
        )}

        <Row className="mb-3">
          <Col>
            <Nav tabs className="nav-tabs-custom">
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === "connections",
                  })}
                  onClick={() => {
                    setActiveTab("connections");
                    setDnsRecordsForDomain(null);
                  }}
                >
                  <i className="mdi mdi-link-variant me-1"></i>
                  Conexiones
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === "domains",
                  })}
                  onClick={() => {
                    setActiveTab("domains");
                    if (selectedProviderKey) {
                      loadDomains(selectedProviderKey);
                    }
                  }}
                >
                  <i className="mdi mdi-domain me-1"></i>
                  Dominios y DNS
                </NavLink>
              </NavItem>
            </Nav>
          </Col>
        </Row>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="connections">
            <Row>
              <Col md={4}>
                <Card>
                  <CardBody>
                    <h5 className="mb-3">
                      <i className="mdi mdi-server-network me-2"></i>
                      Proveedores disponibles
                    </h5>
                    {loadingProviders ? (
                      <Loader size={120} />
                    ) : providers.length === 0 ? (
                      <Alert color="warning">
                        No hay proveedores disponibles. Contacta al administrador.
                      </Alert>
                    ) : (
                      <DataTable
                        data={providers}
                        columns={providerConnectionsColumns}
                        noHeader
                        highlightOnHover
                        pointerOnHover
                        onRowClicked={(row) =>
                          setSelectedProviderKey(
                            row.key || row.id || row.name
                          )
                        }
                        noDataComponent={
                          <span className="py-4">
                            No hay proveedores configurados.
                          </span>
                        }
                      />
                    )}
                  </CardBody>
                </Card>
              </Col>

              <Col md={8}>
                <Card>
                  <CardBody>
                    <h5 className="mb-3">
                      <i className="mdi mdi-cog me-2"></i>
                      Configurar proveedor
                    </h5>
                    {!selectedProviderKey ? (
                      <Alert color="info">
                        <i className="mdi mdi-information-outline me-2"></i>
                        Selecciona un proveedor en la lista de la izquierda para configurar sus credenciales.
                      </Alert>
                    ) : (
                      <>
                        {selectedProvider && (
                          <Alert color="primary" className="mb-3">
                            <strong>{selectedProvider.displayName || selectedProvider.name || selectedProviderKey}</strong>
                            <br />
                            <small>
                              Configura las credenciales de este proveedor para poder enviar correos electrónicos.
                            </small>
                          </Alert>
                        )}
                        <Form
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleSaveProvider(selectedProviderKey);
                          }}
                        >
                          {renderProviderFormFields(selectedProviderKey)}

                          <div className="d-flex gap-2 mt-4">
                            <Button
                              color="primary"
                              type="submit"
                              disabled={savingProvider}
                            >
                              {savingProvider ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" />
                                  Guardando...
                                </>
                              ) : (
                                <>
                                  <i className="mdi mdi-content-save me-2"></i>
                                  Guardar credenciales
                                </>
                              )}
                            </Button>
                            <Button
                              color="success"
                              type="button"
                              disabled={testingConnection}
                              onClick={() =>
                                handleTestConnection(selectedProviderKey)
                              }
                            >
                              {testingConnection ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" />
                                  Probando...
                                </>
                              ) : (
                                <>
                                  <i className="mdi mdi-network me-2"></i>
                                  Probar conexión
                                </>
                              )}
                            </Button>
                          </div>
                        </Form>
                      </>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tabId="domains">
            <Row>
              <Col md={4}>
                <Card>
                  <CardBody>
                    <h5 className="mb-3">
                      <i className="mdi mdi-server me-2"></i>
                      Seleccionar proveedor
                    </h5>
                    {providers.length === 0 ? (
                      <Alert color="warning">
                        <i className="mdi mdi-alert-outline me-2"></i>
                        No hay proveedores disponibles. Configura al menos uno en la pestaña "Conexiones".
                      </Alert>
                    ) : (
                      <FormGroup>
                        <Label className="form-label">Proveedor</Label>
                        <Input
                          type="select"
                          value={selectedProviderKey}
                          onChange={(e) => {
                            setSelectedProviderKey(e.target.value);
                            setDnsRecordsForDomain(null);
                          }}
                        >
                          {providers.map((p) => {
                            const key = p.key || p.id || p.name;
                            return (
                              <option key={key} value={key}>
                                {p.displayName || p.name || key}
                              </option>
                            );
                          })}
                        </Input>
                      </FormGroup>
                    )}

                    <hr />

                    <h5 className="mb-3">
                      <i className="mdi mdi-plus-circle me-2"></i>
                      Agregar nuevo dominio
                    </h5>
                    <Alert color="info" className="small">
                      <i className="mdi mdi-information-outline me-2"></i>
                      Puedes usar un subdominio como <code>mail.tudominio.com</code> para evitar conflictos con otros servicios.
                    </Alert>
                    <Form onSubmit={handleCreateDomain} className="mt-3">
                      <FormGroup className="mb-3">
                        <Label className="form-label">Nombre del dominio</Label>
                        <Input
                          type="text"
                          placeholder="ej. midominio.com o mail.midominio.com"
                          value={newDomain}
                          onChange={(e) => setNewDomain(e.target.value)}
                          disabled={!selectedProviderKey}
                        />
                        <small className="form-text text-muted">
                          Ingresa el dominio que quieres usar para enviar correos.
                        </small>
                      </FormGroup>
                      <Button
                        color="success"
                        type="submit"
                        disabled={!selectedProviderKey || !newDomain}
                        className="w-100"
                      >
                        <i className="mdi mdi-plus me-2"></i>
                        Crear dominio y obtener registros DNS
                      </Button>
                    </Form>
                  </CardBody>
                </Card>
              </Col>

              <Col md={8}>
                <Card>
                  <CardBody>
                    <h5 className="mb-3">
                      <i className="mdi mdi-format-list-bulleted me-2"></i>
                      Dominios configurados
                    </h5>
                    {loadingDomains ? (
                      <Loader size={120} />
                    ) : domains.length === 0 ? (
                      <Alert color="info">
                        <i className="mdi mdi-information-outline me-2"></i>
                        Aún no hay dominios configurados para este proveedor. Agrega uno usando el formulario de la izquierda.
                      </Alert>
                    ) : (
                      <DataTable
                        data={domains}
                        columns={domainColumns}
                        noDataComponent={
                          <span className="py-4">
                            No hay dominios configurados.
                          </span>
                        }
                      />
                    )}
                  </CardBody>
                </Card>

                {claveDominio && (
                  <Card className="mt-3 border-warning">
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                          <i className="mdi mdi-key-variant me-2 text-warning"></i>
                          Clave propia de{" "}
                          <span className="text-warning">
                            {claveDominio.domain}
                          </span>
                        </h5>
                        <Button
                          color="light"
                          size="sm"
                          onClick={() => setClaveDominio(null)}
                        >
                          <i className="mdi mdi-close"></i>
                        </Button>
                      </div>
                      <Alert color="info">
                        <i className="mdi mdi-information-outline me-2"></i>
                        Solo hace falta si este dominio vive en una cuenta del
                        proveedor <strong>distinta</strong> a la de los demás.
                        Déjalo vacío y se usará la clave configurada en el
                        proveedor de la empresa, que es lo normal.
                      </Alert>
                      <FormGroup>
                        <Label for="claveDominioInput">
                          Clave privada del proveedor para este dominio
                        </Label>
                        <Input
                          id="claveDominioInput"
                          type="password"
                          autoComplete="new-password"
                          placeholder="Pégala aquí. Para quitarla, guarda vacío."
                          value={claveDominio.valor}
                          onChange={(e) =>
                            setClaveDominio({
                              ...claveDominio,
                              valor: e.target.value,
                            })
                          }
                        />
                        <small className="text-muted">
                          No se muestra nunca después de guardarla: la tabla solo
                          indica si el dominio tiene clave propia.
                        </small>
                      </FormGroup>
                      <div className="d-flex gap-2">
                        <Button
                          color="warning"
                          disabled={guardandoClave}
                          onClick={guardarClaveDominio}
                        >
                          {guardandoClave ? (
                            <>
                              <i className="mdi mdi-loading mdi-spin me-1"></i>
                              Guardando…
                            </>
                          ) : (
                            <>
                              <i className="mdi mdi-content-save me-1"></i>
                              Guardar clave
                            </>
                          )}
                        </Button>
                        <Button
                          color="light"
                          disabled={guardandoClave}
                          onClick={() => setClaveDominio(null)}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                )}

                {dnsRecordsForDomain && dnsRecordsForDomain.dnsRecords && (
                  <Card className="mt-3 border-primary">
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                          <i className="mdi mdi-dns me-2 text-primary"></i>
                          Registros DNS para{" "}
                          <span className="text-primary">
                            {dnsRecordsForDomain.domain}
                          </span>
                        </h5>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => setDnsRecordsForDomain(null)}
                        >
                          <i className="mdi mdi-close"></i>
                        </Button>
                      </div>
                      <Alert color="warning">
                        <i className="mdi mdi-alert-circle-outline me-2"></i>
                        <strong>Importante:</strong> Añade estos registros en la configuración DNS de tu dominio.
                        La propagación puede tardar entre 24 y 72 horas según tu proveedor de DNS.
                      </Alert>
                      <div className="table-responsive">
                        <table className="table table-bordered align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Tipo</th>
                              <th>Nombre / Host</th>
                              <th>Valor</th>
                              <th>TTL</th>
                              <th>Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.isArray(dnsRecordsForDomain.dnsRecords) &&
                              dnsRecordsForDomain.dnsRecords.map((r, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <span className="badge bg-info">{r.type}</span>
                                  </td>
                                  <td>
                                    <code className="text-dark">{r.name}</code>
                                  </td>
                                  <td>
                                    <code className="text-dark" style={{ wordBreak: "break-all" }}>
                                      {r.value}
                                    </code>
                                  </td>
                                  <td>{r.ttl || "3600"}</td>
                                  <td>
                                    <Button
                                      color="soft-primary"
                                      size="sm"
                                      onClick={() => copyToClipboard(r.value)}
                                      title="Copiar valor"
                                    >
                                      <i className="mdi mdi-content-copy"></i>
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                      <Alert color="success" className="mt-3">
                        <i className="mdi mdi-check-circle me-2"></i>
                        Una vez que hayas añadido los registros DNS, usa el botón "Verificar" en la tabla de dominios para comprobar el estado.
                      </Alert>
                    </CardBody>
                  </Card>
                )}
              </Col>
            </Row>
          </TabPane>
        </TabContent>
      </Container>
    </div>
  );
};

export default EmailProviders;

